import {isSameDay} from 'date-fns';
import {HStack, VStack} from 'platform/foundation';

import {useCallback, KeyboardEvent, useEffect, useRef, FocusEvent} from 'react';

import {suffixTestId, TestIdProps} from 'shared/web';

import {FormControlProps} from '../../../types/FormControlProps';
import {Chip} from '../../Chip/Chip';
import {RelativeDate} from '../RelativeDate';

interface DatePickerRelativeDatesProps extends FormControlProps<Date | null>, TestIdProps {
  relativeDates?: RelativeDate[];
  focusedIndex?: number;
  onFocusChange?: (index: number) => void;
  onBlur?: () => void;
  onEscapeKey?: () => void;
  isFocused?: boolean;
}

export function DatePickerRelativeDates(props: DatePickerRelativeDatesProps) {
  const chipRefs = useRef<(HTMLDivElement | null)[]>([]);

  const relativeDatesLength = props.relativeDates?.length ?? 0;
  const focusedRelativeDayIndex = props.focusedIndex ?? -1;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const newFocusedIndex = (focusedRelativeDayIndex + 1) % relativeDatesLength;
        props.onFocusChange?.(newFocusedIndex);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        const newFocusedIndex =
          focusedRelativeDayIndex === 0 ? relativeDatesLength - 1 : focusedRelativeDayIndex - 1;
        props.onFocusChange?.(newFocusedIndex);
      } else if (event.key === 'Tab') {
        if (event.shiftKey) {
          event.preventDefault();
          props.onBlur?.();
        }
        // Let tab without shift go to next focus zone (handled by focus trap)
      } else if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation(); // Prevent ESC from bubbling to parent modals
        props.onEscapeKey?.();
      }
    },
    [focusedRelativeDayIndex, relativeDatesLength, props]
  );

  const handleChipKeyDown = useCallback(
    (relativeDate: RelativeDate, index: number) => (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        props.onChange?.(relativeDate.date);
      }
    },
    [props]
  );

  // Focus the correct chip when focus index changes
  useEffect(() => {
    if (focusedRelativeDayIndex >= 0) {
      const chipElement = chipRefs.current[focusedRelativeDayIndex];
      if (chipElement) {
        chipElement.focus();
      }
    }
  }, [focusedRelativeDayIndex]);

  if (!props.relativeDates?.length) {
    return null;
  }

  const handleContainerFocus = () => {
    // Focus first chip when container receives focus via Tab
    if (focusedRelativeDayIndex === -1) {
      props.onFocusChange?.(0);
    }
  };

  const handleContainerBlur = (event: FocusEvent<HTMLDivElement>) => {
    // Reset focus index when leaving the container entirely
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      props.onBlur?.();
    }
  };

  return (
    <div
      onKeyDown={handleKeyDown}
      onFocus={handleContainerFocus}
      onBlur={handleContainerBlur}
      tabIndex={props.isFocused ? 0 : -1}
      role="group"
      aria-label="Quick date selection options"
      aria-describedby="relative-dates-instructions"
    >
      <div
        id="relative-dates-instructions"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
        aria-live="polite"
      >
        Use up and down arrow keys to navigate between date options, Enter or Space to select
      </div>
      <VStack spacing={3} data-testid={suffixTestId('relativeDatesContainer', props)}>
        {props.relativeDates.map((relativeDate, index) => {
          const isSelected = !!props.value && isSameDay(props.value, relativeDate.date);
          const ariaLabel = `${relativeDate.label}${isSelected ? ', selected' : ', not selected'}`;

          return (
            <HStack key={`${relativeDate.date.toISOString()}-${relativeDate.label}`}>
              <Chip
                ref={(el) => {
                  chipRefs.current[index] = el;
                }}
                label={relativeDate.label}
                value={isSelected}
                size="small"
                onChange={() => props.onChange?.(relativeDate.date)}
                isFocused={focusedRelativeDayIndex === index}
                onKeyDown={handleChipKeyDown(relativeDate, index)}
                tabIndex={focusedRelativeDayIndex === index ? 0 : -1}
                ariaLabel={ariaLabel}
                data-testid={suffixTestId(`relativeDate-${relativeDate.label}`, props)}
              />
            </HStack>
          );
        })}
      </VStack>
    </div>
  );
}
